// libs/shared/ui/builder/src/lib/components/auth/AgentAuthRegister.tsx
import React, { useRef, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { AuthView } from "./AgentAuthSignIn";

interface Props {
  authServiceUrl: string;
  onNavigate: (view: AuthView) => void;
}

export default function AgentAuthRegister({
  authServiceUrl,
  onNavigate,
}: Props) {
  const [agentId, setAgentId] = useState("");
  const [agentIdError, setAgentIdError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState("");
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (submitted && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [submitted]);

  function validate(): boolean {
    if (!agentId.trim()) {
      setAgentIdError("Agent ID is required");
      return false;
    }
    if (agentId.includes("@")) {
      setAgentIdError("Enter your agent ID, not an email address");
      return false;
    }
    setAgentIdError("");
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setApiError("");
    try {
      const res = await fetch(
        `${authServiceUrl}/service/agents/reset_password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: agentId.trim() }),
        }
      );
      if (!res.ok) {
        setApiError(
          "We could not find an agent with that ID. Please check your agent ID and try again."
        );
        return;
      }
      setSubmitted(true);
    } catch {
      setApiError(
        "Request failed. Please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Stack spacing={2} maxWidth={400} width="100%">
        <Typography
          variant="h5"
          fontWeight={700}
          component="h1"
          tabIndex={-1}
          ref={successHeadingRef}
        >
          Check your email
        </Typography>
        <Typography>
          You should receive an email shortly with your temporary password.
        </Typography>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => onNavigate("signin")}
          sx={{ alignSelf: "flex-start" }}
        >
          Sign in to the agent portal
        </Link>
        <Typography variant="body2" color="text.secondary">
          Did not receive an email? Call{" "}
          <Link
            href="tel:+18665662707"
            aria-label="Call support at (866) 566-2707"
          >
            (866) 566-2707
          </Link>
          , Option 3, then Option 1.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      maxWidth={400}
      width="100%"
      component="form"
      onSubmit={handleSubmit}
      noValidate
      aria-labelledby="register-heading"
      aria-busy={submitting}
    >
      {/* Pre-rendered live region — always in DOM; children toggled */}
      <div role="alert" aria-live="assertive" aria-atomic="true">
        {apiError && <Alert severity="error">{apiError}</Alert>}
      </div>

      <div>
        <Typography
          id="register-heading"
          variant="h5"
          fontWeight={700}
          component="h1"
        >
          Register your agent ID
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Enter your agent ID to set up access. You will receive an email with a
          temporary password.
        </Typography>
      </div>

      <TextField
        label="Agent ID"
        value={agentId}
        onChange={(e) => setAgentId(e.target.value)}
        error={!!agentIdError}
        helperText={agentIdError}
        autoComplete="username"
        fullWidth
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={submitting}
        sx={(theme) => ({
          borderRadius: "5px",
          py: "6px",
          fontSize: "0.875rem",
          backgroundColor: theme.palette.primary.main,
          "&:hover": { backgroundColor: theme.palette.primary.light },
        })}
        startIcon={
          submitting ? (
            <CircularProgress size={14} color="inherit" aria-hidden="true" />
          ) : undefined
        }
      >
        {submitting ? "Submitting…" : "Submit"}
      </Button>

      <Typography variant="body2" textAlign="center">
        Already registered?{" "}
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => onNavigate("signin")}
        >
          Sign in
        </Link>
      </Typography>
    </Stack>
  );
}
